import React, { useState } from 'react';
import { Sentry } from '@/lib/sentry';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Download, Eye, Printer, FileText, CalendarIcon } from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import CreditNoteDocument from './CreditNoteDocument';
import CreditNoteRegularPreview from './CreditNoteRegularPreview';
import { usePdfGeneration } from '@/hooks/usePdfGeneration';
import { toast } from 'sonner';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { cn } from '@/lib/utils';
import { format } from 'date-fns';
import { processDynamicFields } from '@/components/cost-center/document-defaults/dynamicFieldProcessor';

type PreviewMode = 'regular' | 'pdf-style';

interface CreditNotePreviewModalProps {
  creditId: string;
  triggerButton?: React.ReactNode;
  isOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
}

const CreditNotePreviewModal: React.FC<CreditNotePreviewModalProps> = ({
  creditId,
  triggerButton,
  isOpen: controlledIsOpen,
  onOpenChange: controlledOnOpenChange
}) => {
  const [internalIsOpen, setInternalIsOpen] = useState(false);
  const isControlled = controlledIsOpen !== undefined;
  const isOpen = isControlled ? controlledIsOpen : internalIsOpen;
  const setIsOpen = isControlled ? (controlledOnOpenChange || (() => {})) : setInternalIsOpen;
  
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [isPdfLoading, setIsPdfLoading] = useState(false);
  const [previewMode, setPreviewMode] = useState<PreviewMode>('pdf-style');
  const { profile } = useAuth();
  const { generatePdf } = usePdfGeneration();
  const queryClient = useQueryClient();

  const { data: exportData, isLoading, error } = useQuery({
    queryKey: ['creditNoteExportData', creditId],
    queryFn: async () => {
      const { data: credit, error: creditError } = await supabase
        .from('customer_credits')
        .select(`
          *,
          customer:customers!customer_credits_customer_id_fkey(
            name, last_name, customer_type, business_name,
            street_address, suburb, city, province_state, postal_code,
            emails, cell_numbers
          ),
          source_invoice:invoices!customer_credits_source_invoice_id_fkey(invoice_number)
        `)
        .eq('id', creditId)
        .single();

      if (creditError) throw creditError;
      if (!credit.credit_note_number) throw new Error('Credit note has not been generated yet');

      const { data: company, error: companyError } = await supabase
        .from('companies')
        .select('*')
        .eq('id', profile?.company_id)
        .single();

      if (companyError) throw companyError;

      const { data: docDefaults } = await supabase
        .from('document_defaults')
        .select('credit_note_footer')
        .eq('company_id', profile?.company_id!)
        .maybeSingle();

      const { data: userProfileData } = await supabase
        .from('profiles')
        .select('first_name, last_name, email, personal_phone_number')
        .eq('id', profile?.id!)
        .single();

      return {
        creditNote: {
          credit_note_number: credit.credit_note_number,
          credit_amount: credit.credit_amount,
          description: credit.description,
          source_type: credit.source_type,
          status: credit.status,
          credit_note_issued_at: credit.credit_note_issued_at,
          source_invoice: credit.source_invoice,
        },
        customer: credit.customer || { name: 'Unknown', last_name: 'Customer', customer_type: 'residential' } as any,
        company,
        documentDefaults: docDefaults,
        userProfile: userProfileData,
      };
    },
    enabled: isOpen && !!profile?.company_id,
  });

  const currentIssueDate = exportData?.creditNote?.credit_note_issued_at
    ? new Date(exportData.creditNote.credit_note_issued_at)
    : new Date();

  const updateDateMutation = useMutation({
    mutationFn: async (newDate: Date) => {
      const { error } = await supabase
        .from('customer_credits')
        .update({ credit_note_issued_at: newDate.toISOString() })
        .eq('id', creditId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['creditNoteExportData', creditId] });
      queryClient.invalidateQueries({ queryKey: ['credit-details', creditId] });
      toast.success('Issue date updated');
    },
    onError: () => {
      toast.error('Failed to update issue date');
    },
  });

  const handleDateChange = (date: Date | undefined) => {
    if (date) updateDateMutation.mutate(date);
  };

  const computeFooterText = (): string => {
    if (!exportData?.documentDefaults?.credit_note_footer) return '';
    return processDynamicFields(exportData.documentDefaults.credit_note_footer, {
      user: {
        first_name: exportData.userProfile?.first_name,
        last_name: exportData.userProfile?.last_name,
        email: exportData.userProfile?.email,
        personal_phone_number: exportData.userProfile?.personal_phone_number,
      },
      company: {
        name: exportData.company.name,
        email: exportData.company.email,
        company_phone_number: exportData.company.company_phone_number,
      },
      customer: {
        name: `${exportData.customer.name} ${exportData.customer.last_name}`,
        first_name: exportData.customer.name,
        last_name: exportData.customer.last_name,
        email: exportData.customer.emails?.[0],
        cell_numbers: exportData.customer.cell_numbers,
        street_address: exportData.customer.street_address,
        suburb: exportData.customer.suburb,
        city: exportData.customer.city,
        province_state: exportData.customer.province_state,
        postal_code: exportData.customer.postal_code,
      },
      system: {
        today_date: format(new Date(), 'dd MMMM yyyy'),
      },
    });
  };

  const handleGeneratePdf = async (action: 'preview' | 'download') => {
    if (!exportData) {
      toast.error('Credit note data not loaded');
      return;
    }

    const setLoading = action === 'download' ? setIsPdfLoading : setIsPreviewLoading;
    setLoading(true);

    try {
      const previewElement = document.getElementById('credit-note-preview');
      if (!previewElement) {
        toast.error('Preview content not found');
        return;
      }

      const htmlContent = previewElement.innerHTML;
      const footerText = computeFooterText();

      await generatePdf({
        htmlContent,
        estimateData: {
          company: exportData.company,
          userProfile: exportData.userProfile,
          documentDefaults: exportData.documentDefaults,
        },
        filename: `credit-note-${exportData.creditNote.credit_note_number}${action === 'download' ? '.pdf' : '-preview'}`,
        type: 'credit_note',
        action,
        footerText,
      });
    } catch (error) {
      console.error(`Error generating ${action}:`, error);
      toast.error(error instanceof Error ? error.message : `Failed to generate ${action}`);
    } finally {
      setLoading(false);
    }
  };

  const defaultTrigger = (
    <Button variant="outline" size="sm">
      <Printer className="h-4 w-4 mr-2" />
      View/Print Credit Note
    </Button>
  );

  return (
    <Sentry.ErrorBoundary fallback={<div className="p-8 text-center text-destructive">Something went wrong loading the credit note preview.</div>}>
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      {!isControlled && (
        <DialogTrigger asChild>
          {triggerButton || defaultTrigger}
        </DialogTrigger>
      )}
      <DialogContent className="max-w-6xl w-full p-2 md:p-6">
        <DialogHeader>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
            <DialogTitle className="text-base">Credit Note Preview & Download</DialogTitle>
            <div className="flex items-center gap-1.5 modal-actions flex-wrap">
              {/* Issue Date Picker */}
              {exportData && (
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      size="sm"
                      className={cn(
                        "justify-start text-left font-normal h-8",
                        !currentIssueDate && "text-muted-foreground"
                      )}
                      disabled={updateDateMutation.isPending}
                    >
                      <CalendarIcon className="h-3 w-3 sm:mr-1" />
                      <span className="hidden sm:inline text-xs mr-1">Issue Date:</span>
                      <span className="hidden sm:inline">{format(currentIssueDate, 'dd MMMM yyyy')}</span>
                      <span className="sm:hidden">{format(currentIssueDate, 'dd/MM')}</span>
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="end">
                    <Calendar
                      mode="single"
                      selected={currentIssueDate}
                      onSelect={handleDateChange}
                      initialFocus
                      className="p-3 pointer-events-auto"
                    />
                  </PopoverContent>
                </Popover>
              )}
              <div className="flex items-center gap-1 bg-muted rounded-md p-1">
                <Button
                  variant={previewMode === 'regular' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setPreviewMode('regular')}
                  className="h-8 px-2 sm:px-3"
                >
                  <Eye className="h-3 w-3 sm:mr-1" />
                  <span className="hidden sm:inline">Regular</span>
                </Button>
                <Button
                  variant={previewMode === 'pdf-style' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setPreviewMode('pdf-style')}
                  className="h-8 px-2 sm:px-3"
                >
                  <FileText className="h-3 w-3 sm:mr-1" />
                  <span className="hidden sm:inline">PDF Style</span>
                </Button>
              </div>

              <Button
                onClick={() => handleGeneratePdf('download')}
                disabled={isPdfLoading || !exportData}
                size="sm"
                className="h-8"
              >
                <Download className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">{isPdfLoading ? 'Generating...' : 'Save as PDF'}</span>
              </Button>
            </div>
          </div>
        </DialogHeader>
        
        <div className="overflow-auto max-h-[70vh] border rounded-lg mt-4">
          {isLoading && (
            <div className="flex items-center justify-center py-8">
              <p>Loading credit note preview...</p>
            </div>
          )}
          
          {error && (
            <div className="flex items-center justify-center py-8">
              <p className="text-destructive">
                {error instanceof Error ? error.message : 'Error loading credit note preview'}
              </p>
            </div>
          )}
          
          {exportData && (
            <>
              {/* Always render PDF-style for capture; hide when in regular mode */}
              <div style={previewMode !== 'pdf-style' ? { position: 'absolute', left: '-9999px', opacity: 0, pointerEvents: 'none' } : {}}>
                <CreditNoteDocument
                  creditNote={exportData.creditNote}
                  customer={exportData.customer}
                  company={exportData.company}
                  documentDefaults={exportData.documentDefaults}
                  userProfile={exportData.userProfile}
                />
              </div>
              {previewMode === 'regular' && (
                <CreditNoteRegularPreview
                  creditNote={exportData.creditNote}
                  customer={exportData.customer}
                  company={exportData.company}
                />
              )}
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
    </Sentry.ErrorBoundary>
  );
};

export default CreditNotePreviewModal;
